import axios from "axios";
import { getApiBase } from "../apiBase";
import { buildPluginsHeader } from "./registry";
import {
  CRM_CSV_BOOL_FIELDS,
  CRM_CSV_MULTI_FIELDS,
  CRM_CSV_NUM_FIELDS,
  invertCrmMapping,
} from "./crmCsvImportFields";
import { normalizeMulti } from "./commercialProjectSchema";

const AUTH_TOKEN_KEY = "projectBoardAuthToken";
const DEFAULT_MATCH_FIELD = "work_orders";

const LOCATION_FIELD_KEYS = [
  "departments",
  "service_location",
  "connection_types",
  "subtype",
  "a_location",
  "z_location",
  "service_speed",
  "ips",
  "mrc",
  "nrc",
  "location_notes",
];

function authHeaders() {
  const h = { ...buildPluginsHeader() };
  try {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (token) h.Authorization = `Bearer ${token}`;
  } catch {
    /* ignore */
  }
  return h;
}

function toBool(v) {
  if (v === null || v === undefined || v === "") return null;
  if (typeof v === "boolean") return v;
  const s = String(v).trim().toLowerCase();
  if (["1", "true", "yes", "y"].includes(s)) return true;
  if (["0", "false", "no", "n"].includes(s)) return false;
  return null;
}

function toNum(v) {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(String(v).replace(/[$,]/g, ""));
  return Number.isFinite(n) ? n : null;
}

function parseWorkOrderList(raw) {
  if (raw == null || raw === "") return [];
  if (Array.isArray(raw)) {
    return [...new Set(raw.map((x) => String(x || "").trim()).filter(Boolean))];
  }
  return [
    ...new Set(
      String(raw)
        .split(/[,;\s|]+/)
        .map((x) => x.trim())
        .filter(Boolean)
    ),
  ];
}

function coerceFieldValue(fieldId, raw) {
  if (raw === null || raw === undefined) return undefined;
  const s = typeof raw === "string" ? raw.trim() : raw;
  if (s === "") return undefined;
  if (CRM_CSV_BOOL_FIELDS.has(fieldId)) {
    const b = toBool(s);
    return b === null ? undefined : b;
  }
  if (CRM_CSV_NUM_FIELDS.has(fieldId)) {
    const n = toNum(s);
    return n === null ? undefined : n;
  }
  if (CRM_CSV_MULTI_FIELDS.has(fieldId) || fieldId === "work_orders") {
    if (fieldId === "work_orders") {
      const list = parseWorkOrderList(s);
      return list.length ? list.join(",") : undefined;
    }
    const arr = normalizeMulti(s);
    return arr.length ? arr.join(",") : undefined;
  }
  return String(s);
}

/**
 * @param {Record<string, string>} csvRow
 * @param {Record<string, string>} destToColumn
 */
export function mappedCrmRowToPayload(csvRow, destToColumn) {
  const payload = { _source: "import_csv" };
  for (const [fieldId, csvCol] of Object.entries(destToColumn || {})) {
    if (!fieldId || !csvCol) continue;
    const val = coerceFieldValue(fieldId, csvRow[csvCol]);
    if (val !== undefined) payload[fieldId] = val;
  }
  return payload;
}

function normKey(s) {
  return String(s || "")
    .trim()
    .toLowerCase();
}

function titleCompanyKey(title, companyName) {
  const t = normKey(title);
  if (!t) return "";
  const c = normKey(companyName);
  return c ? `${c}::${t}` : `::${t}`;
}

function opportunityWorkOrders(row) {
  const list = [];
  if (Array.isArray(row?.workOrders)) list.push(...row.workOrders);
  if (Array.isArray(row?.projects)) {
    for (const p of row.projects) {
      if (p?.workOrder) list.push(p.workOrder);
    }
  }
  if (row?.workOrder) list.push(row.workOrder);
  return [...new Set(list.map((w) => normKey(w)).filter(Boolean))];
}

function buildIndexes(opportunities) {
  const byId = new Map();
  const byTitleCompany = new Map();
  const byWo = new Map();
  for (const row of opportunities || []) {
    if (row?.id == null) continue;
    byId.set(String(row.id), row);
    const tk = titleCompanyKey(row.title, row.companyName);
    if (tk) byTitleCompany.set(tk, row);
    // Also index title-only for fallback when company not mapped
    const titleOnly = titleCompanyKey(row.title, "");
    if (titleOnly && !byTitleCompany.has(titleOnly)) byTitleCompany.set(titleOnly, row);
    for (const wo of opportunityWorkOrders(row)) {
      if (!byWo.has(wo)) byWo.set(wo, row);
    }
  }
  return { byId, byTitleCompany, byWo };
}

function registerOpportunity(indexes, row) {
  if (!row || row.id == null) return;
  indexes.byId.set(String(row.id), row);
  const tk = titleCompanyKey(row.title, row.companyName);
  if (tk) indexes.byTitleCompany.set(tk, row);
  const titleOnly = titleCompanyKey(row.title, "");
  if (titleOnly) indexes.byTitleCompany.set(titleOnly, row);
  for (const wo of opportunityWorkOrders(row)) {
    indexes.byWo.set(wo, row);
  }
}

function findMatch(indexes, flat, matchField) {
  if (matchField === "id") {
    const id = flat.id != null ? String(flat.id).trim() : "";
    if (id && indexes.byId.has(id)) return indexes.byId.get(id);
    return null;
  }
  if (matchField === "work_orders") {
    const wos = parseWorkOrderList(flat.work_orders);
    for (const wo of wos) {
      const hit = indexes.byWo.get(normKey(wo));
      if (hit) return hit;
    }
    return null;
  }
  if (matchField === "title") {
    const title = flat.title;
    if (!title) return null;
    const withCompany = titleCompanyKey(title, flat.company_name);
    if (withCompany && indexes.byTitleCompany.has(withCompany)) {
      return indexes.byTitleCompany.get(withCompany);
    }
    const titleOnly = titleCompanyKey(title, "");
    return indexes.byTitleCompany.get(titleOnly) || null;
  }
  return null;
}

function flatPayloadToCrmBody(flat) {
  const body = { _source: "import_csv" };
  if (flat.company_name) body.companyName = String(flat.company_name).trim();
  if (flat.title != null) body.title = String(flat.title).trim();
  if (flat.status != null) body.status = String(flat.status).trim();
  if (flat.project_type != null) body.projectType = String(flat.project_type).trim();
  if (flat.new_customer !== undefined) body.newCustomer = Boolean(flat.new_customer);
  if (flat.term_in_months !== undefined) body.termInMonths = flat.term_in_months;
  if (flat.sales_rep != null) body.salesRep = String(flat.sales_rep).trim();
  if (flat.notes != null) body.notes = String(flat.notes).trim();
  if (flat.site_contact_name != null) body.siteContactName = String(flat.site_contact_name).trim();
  if (flat.site_contact_email != null) body.siteContactEmail = String(flat.site_contact_email).trim();
  if (flat.site_contact_phone != null) body.siteContactPhone = String(flat.site_contact_phone).trim();
  if (flat.it_contact_name != null) body.itContactName = String(flat.it_contact_name).trim();
  if (flat.it_contact_email != null) body.itContactEmail = String(flat.it_contact_email).trim();
  if (flat.it_contact_phone != null) body.itContactPhone = String(flat.it_contact_phone).trim();
  if (flat.accounting_contact_name != null) {
    body.accountingContactName = String(flat.accounting_contact_name).trim();
  }
  if (flat.accounting_contact_email != null) {
    body.accountingContactEmail = String(flat.accounting_contact_email).trim();
  }
  if (flat.accounting_contact_phone != null) {
    body.accountingContactPhone = String(flat.accounting_contact_phone).trim();
  }

  const wos = parseWorkOrderList(flat.work_orders);
  if (wos.length) {
    body.workOrders = wos;
    const statusNorm = String(flat.status || "")
      .trim()
      .toLowerCase();
    if (statusNorm === "closed" || statusNorm === "won" || statusNorm === "closed_won") {
      body.status = "closed";
      body.markClosed = true;
    }
  }

  const hasLocation = LOCATION_FIELD_KEYS.some((k) => flat[k] !== undefined && flat[k] !== "");
  if (hasLocation) {
    body.locations = [
      {
        departments: normalizeMulti(flat.departments),
        serviceLocation: flat.service_location || "",
        connectionTypes: normalizeMulti(flat.connection_types),
        subtype: flat.subtype || "",
        aLocation: flat.a_location || "",
        zLocation: flat.z_location || "",
        serviceSpeed: flat.service_speed || "",
        ips: flat.ips || "",
        mrc: flat.mrc,
        nrc: flat.nrc,
        notes: flat.location_notes || "",
      },
    ];
  }

  return body;
}

function hasCreateIdentity(flat, body) {
  if (body.companyName || body.companyId) return true;
  if (flat.company_name) return true;
  // Linking existing WOs alone is not enough without a company — skip
  return false;
}

/**
 * @param {Record<string, string>[]} csvRows
 * @param {Record<string, string>} columnToDest
 * @param {object} options
 */
export async function importCrmOpportunitiesFromCsv(csvRows, columnToDest, options = {}) {
  const matchField = options.matchField || DEFAULT_MATCH_FIELD;
  const createIfMissing = options.createIfMissing !== false;
  const onProgress = options.onProgress;
  const destToColumn = invertCrmMapping(columnToDest);

  const base = getApiBase();
  const headers = authHeaders();
  const existingRes = await axios.get(`${base}/crm/opportunities`, { headers });
  const existing = Array.isArray(existingRes.data) ? existingRes.data : [];
  const indexes = buildIndexes(existing);

  let created = 0;
  let updated = 0;
  let skipped = 0;
  const errors = [];

  for (let i = 0; i < csvRows.length; i += 1) {
    const flat = mappedCrmRowToPayload(csvRows[i], destToColumn);
    const match = findMatch(indexes, flat, matchField);
    const body = flatPayloadToCrmBody(flat);

    try {
      let row = null;
      if (match?.id != null) {
        const { data } = await axios.patch(`${base}/crm/opportunities/${match.id}`, body, { headers });
        row = data;
        registerOpportunity(indexes, row);
        updated += 1;
      } else if (createIfMissing && hasCreateIdentity(flat, body)) {
        const { data } = await axios.post(`${base}/crm/opportunities`, body, { headers });
        row = data;
        registerOpportunity(indexes, row);
        created += 1;
      } else {
        skipped += 1;
      }
    } catch (e) {
      const apiErr = e?.response?.data?.error;
      errors.push(
        typeof apiErr === "string" ? apiErr : e?.message || `Row ${i + 2}: import failed`
      );
    }

    if (typeof onProgress === "function") {
      onProgress({
        current: i + 1,
        total: csvRows.length,
        created,
        updated,
        skipped,
        errors,
      });
    }
  }

  return { total: csvRows.length, created, updated, skipped, errors };
}
